import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Trash2 } from 'lucide-react';
import { cn } from '../../../../../../utils/classnames';
import { camelizeKeys } from '../../../../../../utils/camelizeKeys';
import { useToast } from '../../../../../../components/ui/toast/ToastContext';
import ColorSelect from '../../../../../../components/ui/selects/ColorSelect';

const DEFAULT_COLOR = '#337BA9';

const PRODUCT_OPTIONS = [
  { value: 'carteira_administrada', label: 'Carteira Administrada' },
  { value: 'pgbl', label: 'PGBL BTG' },
  { value: 'vgbl', label: 'VGBL BTG' },
];

const GRID = '36px 1.6fr 1.35fr 1.7fr 1.7fr 42px 96px';
const EDIT_GRID = '68px 1.25fr 1.25fr 1.35fr 1.35fr 90px';

const getProductLabel = (value) => {
  return PRODUCT_OPTIONS.find((option) => option.value === value)?.label || value || '-';
};

const normalizeErrors = (error) => {
  if (error?.errors?.length) return error.errors.join(', ');
  if (error?.error) return error.error;
  if (error?.message) return error.message;

  return 'Ocorreu um erro inesperado';
};

const LabeledInput = ({ label, name, value, onChange, placeholder }) => {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <label className="text-sm text-blueSteel ml-2">{label}</label>

      <input
        name={name}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-white border border-bluePale rounded-xl px-4 py-3 text-base text-blueDarker outline-none transition-colors focus:border-tealBase"
      />
    </div>
  );
};

const LabeledProductSelect = ({ value, onChange }) => {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <label className="text-sm text-blueSteel ml-2">Produto</label>

      <div className="relative">
        <select
          name="product"
          value={value || 'carteira_administrada'}
          onChange={onChange}
          className="appearance-none w-full bg-white border border-bluePale rounded-xl px-4 py-3 pr-10 text-base text-blueDarker outline-none transition-colors focus:border-tealBase"
        >
          {PRODUCT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
          <ChevronDown size={18} className="text-blueSteel" strokeWidth={1.5} />
        </div>
      </div>
    </div>
  );
};

const AllocationRow = ({ allocation, idx, onEdit, onDelete, disabled, canWrite }) => {
  const color = allocation.colorHex || DEFAULT_COLOR;

  return (
    <div className="bg-blueLight rounded-xl">
      <div className="grid items-center gap-4 px-5 py-3 text-sm text-blueSteel" style={{ gridTemplateColumns: GRID }}>
        <div
          className="shrink-0"
          style={{
            width: 20,
            height: 20,
            borderRadius: 6,
            backgroundColor: color,
          }}
        />

        <div className="truncate font-semibold text-blueDarker text-base" title={allocation.name}>
          {allocation.name || '-'}
        </div>

        <div className="truncate" title={getProductLabel(allocation.product)}>
          {getProductLabel(allocation.product)}
        </div>

        <div className="truncate" title={allocation.operation}>
          {allocation.operation || ''}
        </div>

        <div className="truncate" title={allocation.recurrence}>
          {allocation.recurrence || ''}
        </div>

        {canWrite ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onDelete(allocation)}
            className={cn(
              'flex items-center justify-center transition-opacity',
              disabled ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-70'
            )}
          >
            <Trash2 size={18} className="text-redBase" strokeWidth={2} />
          </button>
        ) : (
          <div />
        )}

        {canWrite ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onEdit(allocation)}
            className={cn(
              'bg-tealMedium text-white text-xs font-semibold rounded-full px-5 pt-3 pb-2 transition-colors border-r-2 border-b-2 border-tealBase',
              disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-tealDark'
            )}
          >
            EDITAR
          </button>
        ) : (
          <div />
        )}
      </div>
    </div>
  );
};

const EditableAllocationRow = ({ draft, onChange, onSave, saving, canWrite }) => {
  return (
    <div className="bg-blueLight rounded-xl px-5 py-5">
      <div className="grid items-end gap-5" style={{ gridTemplateColumns: EDIT_GRID }}>
        <div className="flex flex-col gap-1">
          <div style={{ height: 20 }} />
          <ColorSelect
            name="colorHex"
            value={draft.colorHex || DEFAULT_COLOR}
            onChange={onChange}
            dotSize="20px"
            dotRadius="6px"
          />
        </div>

        <LabeledInput label="Nome" name="name" value={draft.name} onChange={onChange} placeholder="Previdência" />

        <LabeledProductSelect value={draft.product} onChange={onChange} />

        <LabeledInput
          label="Operação"
          name="operation"
          value={draft.operation}
          onChange={onChange}
          placeholder="Aplicar R$ 900.000,00"
        />

        <LabeledInput
          label="Recorrência"
          name="recurrence"
          value={draft.recurrence}
          onChange={onChange}
          placeholder="R$ 18.000,00 / mês"
        />

        {canWrite && (
          <button
            type="button"
            disabled={saving}
            onClick={onSave}
            className={cn(
              'bg-tealMedium text-white text-xs font-semibold rounded-full px-5 pt-3 pb-2 transition-colors border-r-2 border-b-2 border-tealBase',
              saving ? 'opacity-50 cursor-not-allowed' : 'hover:bg-tealDark'
            )}
          >
            {saving ? 'SALVANDO' : 'SALVAR'}
          </button>
        )}
      </div>
    </div>
  );
};

const CustomizeAllocations = ({ clientId, scope, canWrite }) => {
  const { addToast } = useToast();

  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

  const baseUrl = useMemo(() => {
    return `/app/planner/client/${clientId}/dashboard/investment_strategy/investment_allocations`;
  }, [clientId]);

  const buildUrl = useCallback(
    (suffix = '') => {
      const query = scope ? `?scope=${encodeURIComponent(scope)}` : '';

      return `${baseUrl}${suffix}${query}`;
    },
    [baseUrl, scope]
  );

  const fetchData = useCallback(() => {
    setLoading(true);

    fetch(buildUrl())
      .then(async (res) => {
        const responseData = await res.json();

        if (!res.ok) {
          throw responseData;
        }

        return responseData;
      })
      .then((responseData) => {
        const camelData = camelizeKeys(responseData);

        setAllocations(camelData.investmentAllocations || []);
      })
      .catch((error) => {
        console.error('Erro ao buscar alocações:', error);

        addToast({
          type: 'error',
          message: 'Erro ao carregar alocações',
        });

        setAllocations([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [addToast, buildUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const buildPayload = (allocationDraft) => {
    return {
      investment_allocation: {
        color_hex: allocationDraft.colorHex || DEFAULT_COLOR,
        name: allocationDraft.name || '',
        product: allocationDraft.product || 'carteira_administrada',
        operation: allocationDraft.operation || '',
        recurrence: allocationDraft.recurrence || '',
      },
    };
  };

  const handleChangeDraft = (event) => {
    const { name, value } = event.target;

    setDraft((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddAllocation = () => {
    if (editingId) {
      addToast({
        type: 'warning',
        message: 'Salve a alocação em edição antes de adicionar outra',
      });

      return;
    }

    const tempId = `new-${Date.now()}`;

    const newAllocation = {
      id: tempId,
      colorHex: DEFAULT_COLOR,
      name: '',
      product: 'carteira_administrada',
      operation: '',
      recurrence: '',
      isNew: true,
    };

    setAllocations((prev) => [...prev, newAllocation]);
    setEditingId(tempId);
    setDraft(newAllocation);
  };

  const handleEditAllocation = (allocation) => {
    if (editingId && editingId !== allocation.id) {
      addToast({
        type: 'warning',
        message: 'Salve a alocação em edição antes de editar outra',
      });

      return;
    }

    setEditingId(allocation.id);
    setDraft({
      ...allocation,
      colorHex: allocation.colorHex || DEFAULT_COLOR,
      product: allocation.product || 'carteira_administrada',
    });
  };

  const handleSaveAllocation = async () => {
    if (!draft) return;

    if (!draft.name?.trim()) {
      addToast({
        type: 'error',
        message: 'Informe o nome da alocação',
      });

      return;
    }

    const isNew = String(draft.id).startsWith('new-');
    const method = isNew ? 'POST' : 'PATCH';
    const url = isNew ? buildUrl() : buildUrl(`/${draft.id}`);

    setSavingId(draft.id);

    try {
      const response = await fetch(url, {
        method,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify(buildPayload(draft)),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw responseData;
      }

      const camelData = camelizeKeys(responseData);
      const savedAllocation = camelData.investmentAllocation;

      setAllocations((prev) => prev.map((allocation) => (allocation.id === draft.id ? savedAllocation : allocation)));

      setEditingId(null);
      setDraft(null);

      addToast({
        type: 'success',
        message: isNew ? 'Investimento adicionado com sucesso' : 'Investimento atualizado com sucesso',
      });
    } catch (error) {
      console.error('Erro ao salvar alocação:', error);

      addToast({
        type: 'error',
        message: normalizeErrors(error),
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteAllocation = async (allocation) => {
    if (String(allocation.id).startsWith('new-')) {
      setAllocations((prev) => prev.filter((item) => item.id !== allocation.id));
      setEditingId(null);
      setDraft(null);
      return;
    }

    setSavingId(allocation.id);

    try {
      const response = await fetch(buildUrl(`/${allocation.id}`), {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw responseData;
      }

      setAllocations((prev) => prev.filter((item) => item.id !== allocation.id));

      if (editingId === allocation.id) {
        setEditingId(null);
        setDraft(null);
      }

      addToast({
        type: 'success',
        message: 'Investimento excluído com sucesso',
      });
    } catch (error) {
      console.error('Erro ao excluir alocação:', error);

      addToast({
        type: 'error',
        message: normalizeErrors(error),
      });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="bg-white rounded-custom20 px-10 py-8 h-full flex flex-col gap-7 min-w-0">
      <div className="flex flex-col">
        <h3 className="text-xl font-semibold text-blueDarker">Personalizar Alocações</h3>
        <h4 className="text-base text-blueSteelDark">Onde eu devo investir?</h4>
      </div>

      <div className="flex flex-col min-h-0">
        <div className="overflow-x-auto">
          <div style={{ minWidth: 980 }}>
            <div
              className="grid items-center gap-4 px-5 pb-3 text-sm font-semibold text-tealDark"
              style={{ gridTemplateColumns: GRID }}
            >
              <div />
              <div>Nome</div>
              <div>Produto</div>
              <div>Operação</div>
              <div>Recorrência</div>
              <div />
              <div />
            </div>

            <div
              className="flex flex-col gap-5 overflow-y-auto pr-1"
              style={{
                maxHeight: 430,
              }}
            >
              {loading ? (
                <div className="bg-blueLight rounded-xl px-5 py-5 text-blueSteelDark">Carregando alocações...</div>
              ) : allocations.length === 0 ? (
                <div className="bg-blueLight rounded-xl px-5 py-5 text-blueSteelDark">
                  Nenhum investimento cadastrado.
                </div>
              ) : (
                allocations.map((allocation, idx) =>
                  editingId === allocation.id ? (
                    <EditableAllocationRow
                      key={allocation.id}
                      draft={draft}
                      onChange={handleChangeDraft}
                      onSave={handleSaveAllocation}
                      saving={savingId === allocation.id}
                      canWrite={canWrite}
                    />
                  ) : (
                    <AllocationRow
                      key={allocation.id}
                      allocation={allocation}
                      idx={idx}
                      onEdit={handleEditAllocation}
                      onDelete={handleDeleteAllocation}
                      disabled={savingId === allocation.id}
                      canWrite={canWrite}
                    />
                  )
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {canWrite && (
        <div>
          <button
            type="button"
            onClick={handleAddAllocation}
            disabled={loading || !!savingId}
            className={cn(
              'bg-tealMedium text-white text-sm font-semibold rounded-xl px-6 py-3 transition-colors border-r-2 border-b-2 border-tealBase',
              loading || savingId ? 'opacity-50 cursor-not-allowed' : 'hover:bg-tealDark'
            )}
          >
            + ADICIONAR INVESTIMENTO
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomizeAllocations;
